/**
 * Raggruppamento dell'elenco partite (V16).
 *
 * In testa cio che chiede un'azione adesso: la partita in corso, poi le
 * `programmata` rimaste indietro, che vanno concluse o annullate perche
 * sporcherebbero le statistiche di convocazione. Sotto il calendario: le
 * prossime dalla piu vicina, le giocate dalla piu recente.
 */

import { dataLocale } from '@calcio/shared';

export type StatoPartita = 'programmata' | 'formazione' | 'in_corso' | 'conclusa' | 'annullata';

export interface PartitaInElenco {
  id: string;
  dataOra: string;
  avversario: string;
  inCasa: boolean;
  luogo: string | null;
  stato: StatoPartita;
  competizione: string;
  golFatti: number;
  golSubiti: number;
}

export interface Gruppi {
  inCorso: PartitaInElenco[];
  daChiudere: PartitaInElenco[];
  prossime: PartitaInElenco[];
  giocate: PartitaInElenco[];
}

export function raggruppa(partite: readonly PartitaInElenco[], adesso: Date): Gruppi {
  // "Data passata" e il giorno italiano, non l'ora: la partita delle 15 alle
  // 17 e ancora da giocare o da chiudere, non gia dimenticata.
  const oggi = dataLocale(adesso);

  const perData = [...partite].sort(
    (a, b) => new Date(a.dataOra).getTime() - new Date(b.dataOra).getTime(),
  );
  const gruppi: Gruppi = { inCorso: [], daChiudere: [], prossime: [], giocate: [] };

  for (const p of perData) {
    if (p.stato === 'formazione' || p.stato === 'in_corso') gruppi.inCorso.push(p);
    else if (p.stato === 'programmata') {
      if (dataLocale(p.dataOra) < oggi) gruppi.daChiudere.push(p);
      else gruppi.prossime.push(p);
    } else gruppi.giocate.push(p);
  }
  gruppi.giocate.reverse();
  return gruppi;
}

/** Lo stato come si mostra: una programmata con il giorno passato e da chiudere. */
export type Segno = StatoPartita | 'daChiudere';

export const segnoDi = (p: Pick<PartitaInElenco, 'stato' | 'dataOra'>, oggi: string): Segno =>
  p.stato === 'programmata' && dataLocale(p.dataOra) < oggi ? 'daChiudere' : p.stato;

export const ETICHETTA_SEGNO: Record<Segno, string> = {
  programmata: 'Programmata',
  formazione: 'Formazione pronta',
  in_corso: 'In corso',
  conclusa: 'Conclusa',
  annullata: 'Annullata',
  daChiudere: 'Da chiudere',
};

/**
 * Fondo e testo del badge di stato, con i colori fissi degli stati del
 * giocatore (`--stato-*` in `src/index.css`) come negli allenamenti: verde la
 * conclusa, arancio cio che aspetta un gesto, giallo la formazione pronta e
 * rosso la partita in corso.
 */
export const COLORE_SEGNO: Record<Segno, string> = {
  programmata: 'bg-primary text-primary-foreground',
  formazione: 'bg-stato-affaticato text-black',
  in_corso: 'bg-stato-infortunato text-white',
  conclusa: 'bg-stato-disponibile text-white',
  annullata: 'bg-muted text-muted-foreground',
  daChiudere: 'bg-stato-recupero text-black',
};

/** Il pallino dello stesso colore, nel calendario e nella legenda. */
export const PALLINO_SEGNO: Record<Segno, string> = {
  programmata: 'bg-primary',
  formazione: 'bg-stato-affaticato',
  in_corso: 'bg-stato-infortunato',
  conclusa: 'bg-stato-disponibile',
  annullata: 'bg-muted-foreground',
  daChiudere: 'bg-stato-recupero',
};

export type Esito = 'vinta' | 'pareggiata' | 'persa';

export const esitoDi = (p: Pick<PartitaInElenco, 'golFatti' | 'golSubiti'>): Esito =>
  p.golFatti > p.golSubiti ? 'vinta' : p.golFatti < p.golSubiti ? 'persa' : 'pareggiata';

export const ETICHETTA_ESITO: Record<Esito, string> = {
  vinta: 'Vinta',
  pareggiata: 'Pareggiata',
  persa: 'Persa',
};

export const LETTERA_ESITO: Record<Esito, string> = { vinta: 'V', pareggiata: 'N', persa: 'P' };

export const COLORE_ESITO: Record<Esito, string> = {
  vinta: 'bg-stato-disponibile text-white',
  pareggiata: 'bg-muted-foreground text-background',
  persa: 'bg-stato-infortunato text-white',
};

export interface RiepilogoStagione {
  vinte: number;
  pareggiate: number;
  perse: number;
  golFatti: number;
  golSubiti: number;
  /** Da giocare: programmate da oggi in avanti e con la formazione pronta. */
  inProgramma: number;
  daChiudere: number;
}

/** I numeri della testata, sulle partite di tutta la stagione: il risultato solo delle concluse. */
export function riepilogoStagione(
  elenco: readonly PartitaInElenco[],
  adesso: Date,
): RiepilogoStagione {
  const oggi = dataLocale(adesso);
  const r: RiepilogoStagione = {
    vinte: 0,
    pareggiate: 0,
    perse: 0,
    golFatti: 0,
    golSubiti: 0,
    inProgramma: 0,
    daChiudere: 0,
  };
  for (const p of elenco) {
    const segno = segnoDi(p, oggi);
    if (segno === 'conclusa') {
      const esito = esitoDi(p);
      if (esito === 'vinta') r.vinte++;
      else if (esito === 'persa') r.perse++;
      else r.pareggiate++;
      r.golFatti += p.golFatti;
      r.golSubiti += p.golSubiti;
    } else if (segno === 'daChiudere') r.daChiudere++;
    else if (segno === 'programmata' || segno === 'formazione') r.inProgramma++;
  }
  return r;
}
